import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CompanyProfilePage from "@/pages/CompanyProfilePage";
import UsersPage from "@/pages/UsersPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import TransactionsPage from "@/pages/TransactionsPage";
import NewTransactionPage from "@/pages/NewTransactionPage";
import TransactionDetailPage from "@/pages/TransactionDetailPage";
import {
  BookkeepingPage, ApArPage, CashbookPage, PayrollPage, MasterPage,
} from "@/pages/AccountingPages";
import ReportsPage from "@/pages/ReportsPage";
import CompliancePage from "@/pages/CompliancePage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import DocumentsPage from "@/pages/DocumentsPage";
// Phase 3 (2026-08-09) — TasksPage, ControlsPage, FinanceIntegrityPage,
// ManualInvoicesPage removed from the sidebar. Imports dropped so the
// bundle doesn't ship dead code. Route files below redirect to
// /dashboard; component files kept in the tree for reversibility.
import ObserverDashboardPage from "@/pages/ObserverDashboardPage";
import OYAKRepairPage from "@/pages/OYAKRepairPage";
import AutoHealLogPage from "@/pages/AutoHealLogPage";
import AdminReviewQueuePage from "@/pages/AdminReviewQueuePage";
import TaxesPage from "@/pages/TaxesPage";
import TaxSettingsPage from "@/pages/TaxSettingsPage";
import SettingsPage from "@/pages/SettingsPage";
import BudgetSettingsPage from "@/pages/BudgetSettingsPage";
import RolePermissionsPage from "@/pages/RolePermissionsPage";
import LoansPage from "@/pages/LoansPage";
import AccountingHubPage from "@/pages/AccountingHubPage";
import BankPage from "@/pages/BankPage";
import {
  ExpensesBreakdownPage, IncomeBreakdownPage, NetBalanceDrillDownPage,
  CashDrillDownPage, BankBalanceDrillDownPage, PayablesDrillDownPage,
  ReceivablesDrillDownPage, PayrollPendingDrillDownPage,
} from "@/pages/DashboardDrillDowns";
import InvoicesPage, { InvoiceFormPage, InvoiceDetailPage } from "@/pages/InvoicesPage";
import SecurityCenterPage from "@/pages/SecurityCenterPage";
import ProfileSecurityPage from "@/pages/ProfileSecurityPage";
import { ROLES, homePathFor, SOMTURK_ROLES } from "@/lib/permissions";
import { toast } from "sonner";

function RoleGuard({ allowed, children }) {
  const { user } = useAuth();
  const { pathname } = window.location;
  if (!user) return null;
  // Bug bundle #12 (2026-08-19) — full_access users bypass every static
  // role allow-list, matching the backend override.
  if (user.full_access === true) return children;
  if (!allowed.includes(user.role)) {
    // Bug bundle #12 (2026-08-19) — include the attempted path in the
    // toast so users landing back on /dashboard understand what was
    // actually denied.
    setTimeout(() => toast.error(`You don't have access to ${pathname}.`), 50);
    return <Navigate to={homePathFor(user)} replace />;
  }
  return children;
}

// Phase 19 (2026-07-06) — permission-key gate. Use this for any route whose
// access is governed by the granular permission system rather than a static
// role allow-list. `requires` is a list of keys — all must be true.
function PermissionGuard({ requires, children }) {
  const { user, can } = useAuth();
  const { pathname } = window.location;
  if (!user) return null;
  // Bug bundle #12 (2026-08-19) — `can()` already short-circuits on
  // full_access; keeping the explicit check here for parity with RoleGuard.
  if (user.full_access === true) return children;
  const ok = (requires || []).every((k) => can && can(k));
  if (!ok) {
    setTimeout(() => toast.error(`You don't have access to ${pathname}.`), 50);
    return <Navigate to={homePathFor(user)} replace />;
  }
  return children;
}

function Shell({ children, title }) {
  return <AppLayout title={title}>{children}</AppLayout>;
}

function Router() {
  const { user, loading } = useAuth();
  if (loading) return null;

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Navigate to={user ? homePathFor(user) : "/login"} replace />} />

      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Shell title="Dashboard"><DashboardPage /></Shell>
        </ProtectedRoute>
      } />
      {/* Phase 30 (2026-07-17) — /observer-dashboard is a legacy alias.
          All roles land on the unified DashboardPage; role-aware tile
          filtering happens inside the component. */}
      <Route path="/observer-dashboard" element={
        <ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>
      } />
      {/* P1 (2026-07-12) — Dashboard tile drill-downs */}
      <Route path="/dashboard/expenses" element={
        <ProtectedRoute><Shell title="Total Expenses — Breakdown"><ExpensesBreakdownPage /></Shell></ProtectedRoute>
      } />
      <Route path="/dashboard/income" element={
        <ProtectedRoute><Shell title="Total Income — Breakdown"><IncomeBreakdownPage /></Shell></ProtectedRoute>
      } />
      <Route path="/dashboard/net-balance" element={
        <ProtectedRoute><Shell title="Net Balance"><NetBalanceDrillDownPage /></Shell></ProtectedRoute>
      } />
      <Route path="/dashboard/cash" element={
        <ProtectedRoute><Shell title="Cash"><CashDrillDownPage /></Shell></ProtectedRoute>
      } />
      <Route path="/dashboard/bank-balance" element={
        <ProtectedRoute><Shell title="Bank Balance"><BankBalanceDrillDownPage /></Shell></ProtectedRoute>
      } />
      <Route path="/dashboard/payables" element={
        <ProtectedRoute><Shell title="Payables Due"><PayablesDrillDownPage /></Shell></ProtectedRoute>
      } />
      <Route path="/dashboard/receivables" element={
        <ProtectedRoute><Shell title="Receivables Due"><ReceivablesDrillDownPage /></Shell></ProtectedRoute>
      } />
      <Route path="/dashboard/payroll-pending" element={
        <ProtectedRoute><Shell title="Pending Payroll"><PayrollPendingDrillDownPage /></Shell></ProtectedRoute>
      } />
      <Route path="/company" element={
        <ProtectedRoute><Shell title="Company Profile"><CompanyProfilePage /></Shell></ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={[ROLES.SYSTEM_ADMIN]}>
          <Shell title="Users"><UsersPage /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/audit-logs" element={
        <ProtectedRoute allowedRoles={[ROLES.SYSTEM_ADMIN, ROLES.INTERNAL_AUDITOR]}>
          <Shell title="Audit Logs"><AuditLogsPage /></Shell>
        </ProtectedRoute>
      } />

      {/* Transactions (Phase 2) */}
      <Route path="/transactions" element={
        <ProtectedRoute><Shell title="Transactions"><TransactionsPage /></Shell></ProtectedRoute>
      } />
      {/* Phase 6 (2026-08-09) — Unified "+ Create JE" is now the only
          entry point on the Transactions page. The old NewTransactionPage
          and BulkTransactionsPage routes redirect to /transactions so any
          stale bookmarks / notifications land on the unified list.
          Component files are kept in the tree for reversibility. */}
      <Route path="/transactions/new" element={
        <ProtectedRoute><Navigate to="/transactions" replace /></ProtectedRoute>
      } />
      <Route path="/transactions/bulk" element={
        <ProtectedRoute><Navigate to="/transactions" replace /></ProtectedRoute>
      } />
      {/* Bug bundle #14 (2026-08-19) — legacy pre-Phase-53 admin override
          edit page is retired. New editability workflow (Edit metadata + Reverse
          & Correct) is the only supported path, both surfaced on the JE detail
          modal. Old URL redirects to the transactions list. */}
      <Route path="/transactions/:id/edit" element={
        <ProtectedRoute><Navigate to="/transactions" replace /></ProtectedRoute>
      } />
      <Route path="/transactions/:id" element={
        <ProtectedRoute><Shell title="Transaction"><TransactionDetailPage /></Shell></ProtectedRoute>
      } />

      {/* Phase 9 — Accounting Records hub.
          Phase 19 (2026-07-06) — switched from RoleGuard to PermissionGuard so
          the granular `accounting.records.view` key (which the sidebar item
          already requires) is the single source of truth. */}
      <Route path="/accounting" element={
        <ProtectedRoute>
          <PermissionGuard requires={["accounting.section.access", "accounting.records.view"]}>
            <Shell title="Accounting Records"><AccountingHubPage /></Shell>
          </PermissionGuard>
        </ProtectedRoute>
      } />

      {/* Placeholders for later phases */}
      {[
      ].map((r) => (
        <Route key={r.path} path={r.path} element={
          <ProtectedRoute>
            <Shell title={r.title}><PlaceholderPage title={r.title} /></Shell>
          </ProtectedRoute>
        } />
      ))}

      {/* Phase 9d — Settings (per-user password change for everyone) */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <Shell title="Settings"><SettingsPage /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/settings/role-permissions" element={
        <ProtectedRoute>
          <RoleGuard allowed={[ROLES.SYSTEM_ADMIN]}>
            <Shell title="Role Permissions"><RolePermissionsPage /></Shell>
          </RoleGuard>
        </ProtectedRoute>
      } />

      {/* Phase 45 (2026-07-28) - Budget Settings */}
      <Route path="/settings/budgets" element={
        <ProtectedRoute>
          <Shell title="Budget Settings"><BudgetSettingsPage /></Shell>
        </ProtectedRoute>
      } />

      {/* Phase 20 (2026-07-10) — Finance Integrity Check dashboard.
          Phase 3 (2026-08-09) — Removed from sidebar; route now
          redirects to /dashboard. Component file kept for reversibility. */}
      <Route path="/finance-integrity" element={
        <ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>
      } />

      {/* Phase 4 — Reports, Approvals, Compliance, Documents, Tasks */}
      <Route path="/reports" element={
        <ProtectedRoute><Shell title="Financial Reports"><ReportsPage /></Shell></ProtectedRoute>
      } />
      <Route path="/approvals" element={
        <ProtectedRoute><Shell title="Approvals"><ApprovalsPage /></Shell></ProtectedRoute>
      } />
      <Route path="/compliance" element={
        <ProtectedRoute><Shell title="Compliance Procedures"><CompliancePage /></Shell></ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute><Shell title="Documents"><DocumentsPage /></Shell></ProtectedRoute>
      } />
      {/* Phase 3 (2026-08-09) — Tasks and Follow Up removed from sidebar;
          route redirects to /dashboard. Component file kept for reversibility. */}
      <Route path="/tasks" element={
        <ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>
      } />

      {/* Phase 5 — Observer Dashboard, Internal Controls */}
      {/* Phase 30 (2026-07-17) — Observer/Data Entry now redirect to the
          unified /dashboard. The old ObserverDashboardPage remains in
          the tree for backwards compatibility but no longer routed. */}
      <Route path="/observer" element={
        <ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>
      } />
      {/* Phase 3 (2026-08-09) — Internal Controls removed from sidebar;
          route redirects to /dashboard. Component file kept for reversibility. */}
      <Route path="/controls" element={
        <ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>
      } />

      {/* Phase 36 (2026-07-17) — Admin-only OYAK repair action */}
      <Route path="/admin/repair/oyak" element={
        <ProtectedRoute>
          <RoleGuard allowed={[ROLES.SYSTEM_ADMIN]}>
            <Shell title="OYAK Loan Repair"><OYAKRepairPage /></Shell>
          </RoleGuard>
        </ProtectedRoute>
      } />

      {/* Phase 41 (2026-07-28) — Data Integrity Auto-Heal Engine */}
      <Route path="/admin/auto-heal-log" element={
        <ProtectedRoute>
          <RoleGuard allowed={[ROLES.SYSTEM_ADMIN]}>
            <Shell title="Auto-Heal Log"><AutoHealLogPage /></Shell>
          </RoleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/review-queue" element={
        <ProtectedRoute>
          <RoleGuard allowed={[ROLES.SYSTEM_ADMIN]}>
            <Shell title="Admin Review Queue"><AdminReviewQueuePage /></Shell>
          </RoleGuard>
        </ProtectedRoute>
      } />

      {/* Phase 6 — Taxes. Phase 19 (2026-07-06) — permission-gated. */}
      <Route path="/taxes" element={
        <ProtectedRoute>
          <PermissionGuard requires={["taxes.section.access", "taxes.view"]}>
            <Shell title="Taxes"><TaxesPage /></Shell>
          </PermissionGuard>
        </ProtectedRoute>
      } />
      <Route path="/settings/taxes" element={
        <ProtectedRoute><Shell title="Tax Settings"><TaxSettingsPage /></Shell></ProtectedRoute>
      } />
      {/* Bug bundle #15 (2026-08-19) — friendly alias: users commonly type
          `/tax-settings` (with a hyphen) which is intuitive but did not
          match the canonical `/settings/taxes` route, so it fell through to
          the catch-all `*` route below and redirected to /dashboard. */}
      <Route path="/tax-settings" element={
        <ProtectedRoute><Navigate to="/settings/taxes" replace /></ProtectedRoute>
      } />

      {/* Phase 3 — accounting modules */}
      <Route path="/bookkeeping" element={<ProtectedRoute><Shell title="Bookkeeping"><BookkeepingPage /></Shell></ProtectedRoute>} />
      <Route path="/payables" element={<ProtectedRoute><Shell title="Accounts Payable"><ApArPage kind="ap" /></Shell></ProtectedRoute>} />
      <Route path="/receivables" element={<ProtectedRoute><Shell title="Accounts Receivable"><ApArPage kind="ar" /></Shell></ProtectedRoute>} />
      <Route path="/bank" element={<ProtectedRoute><Shell title="Bank and Cash"><BankPage /></Shell></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Shell title="Connected Invoices"><InvoicesPage /></Shell></ProtectedRoute>} />
      <Route path="/invoices/new" element={<ProtectedRoute><Shell title="New Invoice"><InvoiceFormPage /></Shell></ProtectedRoute>} />
      <Route path="/invoices/:id" element={<ProtectedRoute><Shell title="Invoice"><InvoiceDetailPage /></Shell></ProtectedRoute>} />
      <Route path="/invoices/:id/edit" element={<ProtectedRoute><Shell title="Edit Invoice"><InvoiceFormPage /></Shell></ProtectedRoute>} />
      {/* Phase 3 (2026-08-09) — Manual Invoices removed from sidebar; the
          list, new, and detail routes all redirect to /dashboard.
          Component files kept for reversibility. */}
      <Route path="/manual-invoices" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
      <Route path="/manual-invoices/new" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
      <Route path="/manual-invoices/:id" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
      <Route path="/petty-cash" element={<ProtectedRoute><Shell title="Petty Cash"><CashbookPage /></Shell></ProtectedRoute>} />
      <Route path="/payroll" element={<ProtectedRoute><Shell title="Payroll Accounting"><PayrollPage /></Shell></ProtectedRoute>} />
      <Route path="/loans" element={
        <ProtectedRoute>
          <PermissionGuard requires={["accounting.section.access", "accounting.loans.view"]}>
            <Shell title="Loans & Borrowings"><LoansPage /></Shell>
          </PermissionGuard>
        </ProtectedRoute>
      } />
      <Route path="/suppliers" element={<ProtectedRoute><Shell title="Suppliers"><MasterPage kind="suppliers" title="Suppliers" extra={["service_type","notes"]} /></Shell></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Shell title="Customers"><MasterPage kind="customers" title="Customers" extra={["notes"]} /></Shell></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute><Shell title="Employees"><MasterPage kind="employees" title="Employees" extra={["position","department","base_salary"]} /></Shell></ProtectedRoute>} />


      {/* Phase A (2026-08-20) — Security */}
      <Route path="/security" element={
        <ProtectedRoute>
          <RoleGuard allowed={[ROLES.SYSTEM_ADMIN]}>
            <Shell title="Security Center"><SecurityCenterPage /></Shell>
          </RoleGuard>
        </ProtectedRoute>
      } />
      <Route path="/profile/security" element={
        <ProtectedRoute>
          <Shell title="Security"><ProfileSecurityPage /></Shell>
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

import { ThemeProvider } from "@/theme/ThemeProvider";
import { DashboardActionsProvider } from "@/context/DashboardActionsContext";

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <DashboardActionsProvider>
            <Router />
            <Toaster position="top-right" richColors closeButton />
          </DashboardActionsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
